import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  useArchiveUserMutation,
  useDeleteUserMutation,
  useGetUsersPaginatedWithSearchQuery,
  useLazyGetUserByIdQuery,
  useUnarchiveUserMutation,
  User,
} from "../../api/usersApi";
import AuditHistoryDialog from "../audit/components/AuditHistoryDialog";
import ArchiveUserDialog from "./components/ArchiveUserDialog";
import DeleteUserDialog from "./components/DeleteUserDialog";
import UsersList from "./components/UsersList";
import { AddButton } from "../../components/common/AddButton";
import PageTitle from "../../components/common/PageTitle";
import TabbedPageCard from "../../components/common/TabbedPageCard";
import { UserIcon } from "../../components/common/pageIcons";
import { useAppSelector } from "../../app/hooks";
import { UserRoles, type UserRole } from "../../constants/roles";
import { DEFAULT_DATA_TABLE_PAGE_SIZE } from "../../constants/dataTable";
import PageLayout from "../../components/layout/PageLayout";
import { useDataTablePagination } from "../../hooks/useDataTablePagination";
import { getRoleDisplayName } from "../../utils/roleDisplayMapper";

const ADMIN_USER_ROLE_TABS: UserRole[] = [
  UserRoles.ADMIN,
  UserRoles.DISPATCHER,
  UserRoles.TRUCK_DRIVER,
  UserRoles.FLEET,
  UserRoles.SAFETY,
  UserRoles.RECRUITING,
  UserRoles.ACCOUNTING,
  UserRoles.IFTA_ADMIN,
];

interface UsersTabContentProps {
  statusFilter?: "Archived";
  roleFilter?: UserRole;
  isAdminOrSuperAdmin: boolean;
  title: string;
  onEditClick: (user: User) => void;
  onDeleteClick: (user: User) => void;
  onArchiveClick: (user: User) => void;
  onRestoreClick: (user: User) => void;
  onHistoryClick: (user: User) => void;
}

const UsersTabContent: React.FC<UsersTabContentProps> = ({
  statusFilter,
  roleFilter,
  isAdminOrSuperAdmin,
  title,
  onEditClick,
  onDeleteClick,
  onArchiveClick,
  onRestoreClick,
  onHistoryClick,
}) => {
  const baseFilters = useMemo(() => {
    const filters: Record<string, any> = {};

    if (roleFilter) {
      filters.role = roleFilter;
    }

    // Non-admin callers always see only TruckDriver users
    if (!isAdminOrSuperAdmin) {
      filters.role = UserRoles.TRUCK_DRIVER;
    }

    if (statusFilter) {
      filters.status = statusFilter;
    }

    return Object.keys(filters).length > 0 ? filters : undefined;
  }, [isAdminOrSuperAdmin, roleFilter, statusFilter]);

  const {
    items: users,
    isLoading,
    isLoadingRows,
    isLoadingMore,
    totalCount,
    hasNextPage,
    handleSearchChange,
    handleLoadMore,
  } = useDataTablePagination<User>(
    useGetUsersPaginatedWithSearchQuery,
    DEFAULT_DATA_TABLE_PAGE_SIZE,
    { baseFilters }
  );

  return (
    <UsersList
      users={users}
      isLoading={isLoading}
      onEditClick={onEditClick}
      onDeleteClick={onDeleteClick}
      onArchiveClick={onArchiveClick}
      onRestoreClick={onRestoreClick}
      onHistoryClick={onHistoryClick}
      isAdminOrSuperAdmin={isAdminOrSuperAdmin}
      title={title}
      totalCount={totalCount}
      hasNextPage={hasNextPage}
      isLoadingMore={isLoadingMore}
      isLoadingRows={isLoadingRows}
      onLoadMore={handleLoadMore}
      enableSearch={true}
      showRoleFilter={isAdminOrSuperAdmin && !roleFilter}
      onSearchChange={handleSearchChange}
    />
  );
};

const UsersPage: React.FC = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [archiveMode, setArchiveMode] = useState<"archive" | "restore">("archive");
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [historyDriver, setHistoryDriver] = useState<{
    userId: number;
    employeeDriverInfoId?: number;
    isLoading: boolean;
    title: string;
    unavailableMessage?: string;
  } | null>(null);

  const currentUserRole = useAppSelector((state) => state.auth.user?.role);
  const isAdminOrSuperAdmin =
    currentUserRole === UserRoles.ADMIN || currentUserRole === UserRoles.SUPER_ADMIN;

  const pageTitle = isAdminOrSuperAdmin ? "Users" : "Drivers";
  const addButtonText = isAdminOrSuperAdmin ? "Add User" : "Add Driver";

  const [deleteUser, { isLoading: isDeleting }] = useDeleteUserMutation();
  const [archiveUser, { isLoading: isArchiving }] = useArchiveUserMutation();
  const [unarchiveUser, { isLoading: isRestoring }] = useUnarchiveUserMutation();
  const [getUserById] = useLazyGetUserByIdQuery();

  const handleEditClick = (user: User) => {
    navigate(`/users/${user.id}/edit`);
  };

  const handleDeleteClick = (user: User) => {
    setSelectedUser(user);
    setIsDeleteModalOpen(true);
  };

  const handleArchiveClick = (user: User) => {
    setSelectedUser(user);
    setArchiveMode("archive");
    setIsArchiveModalOpen(true);
  };

  const handleRestoreClick = (user: User) => {
    setSelectedUser(user);
    setArchiveMode("restore");
    setIsArchiveModalOpen(true);
  };

  const handleHistoryClick = async (user: User) => {
    const title = `History for ${user.firstName} ${user.lastName}`.trim();
    setHistoryDriver({
      userId: user.id,
      isLoading: true,
      title,
    });

    try {
      const userDetails = await getUserById(user.id).unwrap();
      const employeeDriverInfoId = userDetails.driverInfo?.employeeId;

      if (!employeeDriverInfoId) {
        console.warn("Driver history unavailable: missing driver info for user", user.id);
        setHistoryDriver((current) =>
          current?.userId === user.id
            ? {
                ...current,
                isLoading: false,
                unavailableMessage:
                  "Driver history is unavailable because driver details could not be loaded.",
              }
            : current
        );
        return;
      }

      setHistoryDriver((current) =>
        current?.userId === user.id
          ? {
              ...current,
              employeeDriverInfoId,
              isLoading: false,
            }
          : current
      );
    } catch (error) {
      console.error("Failed to load driver details for audit history:", error);
      setHistoryDriver((current) =>
        current?.userId === user.id
          ? {
              ...current,
              isLoading: false,
              unavailableMessage:
                "Driver history could not be loaded. Please try again.",
            }
          : current
      );
    }
  };

  const handleCreateUser = () => {
    navigate("/users/create");
  };

  const handleDeleteConfirm = async () => {
    if (selectedUser) {
      try {
        await deleteUser(selectedUser.id).unwrap();
        setIsDeleteModalOpen(false);
        setSelectedUser(null);
      } catch (error) {
        console.error("Failed to delete user:", error);
      }
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
    setSelectedUser(null);
  };

  const handleArchiveConfirm = async () => {
    if (!selectedUser) return;

    try {
      if (archiveMode === "archive") {
        await archiveUser(selectedUser.id).unwrap();
      } else {
        await unarchiveUser(selectedUser.id).unwrap();
      }
      setIsArchiveModalOpen(false);
      setSelectedUser(null);
    } catch (error) {
      console.error(`Failed to ${archiveMode} user:`, error);
    }
  };

  const handleArchiveCancel = () => {
    setIsArchiveModalOpen(false);
    setSelectedUser(null);
  };

  const renderUsersTab = (
    title: string,
    tabKey: string,
    filters?: Pick<UsersTabContentProps, "roleFilter" | "statusFilter">
  ) => (
    <UsersTabContent
      key={tabKey}
      {...filters}
      isAdminOrSuperAdmin={isAdminOrSuperAdmin}
      title={title}
      onEditClick={handleEditClick}
      onDeleteClick={handleDeleteClick}
      onArchiveClick={handleArchiveClick}
      onRestoreClick={handleRestoreClick}
      onHistoryClick={handleHistoryClick}
    />
  );

  const tabs = isAdminOrSuperAdmin
    ? [
        {
          label: "All Users",
          content: renderUsersTab("All Users", "all"),
        },
        ...ADMIN_USER_ROLE_TABS.map((role) => {
          const roleDisplayName = getRoleDisplayName(role);

          return {
            label: roleDisplayName,
            content: renderUsersTab(`${roleDisplayName} Users`, role, {
              roleFilter: role,
            }),
          };
        }),
        {
          label: "Archived",
          content: renderUsersTab("Archived Users", "archived", {
            statusFilter: "Archived",
          }),
        },
      ]
    : [
        {
          label: "Active",
          content: renderUsersTab(pageTitle, "active"),
        },
        {
          label: "Archived",
          content: renderUsersTab(pageTitle, "archived", {
            statusFilter: "Archived",
          }),
        },
      ];

  return (
    <PageLayout variant="fullHeight">
      <PageTitle
        title={pageTitle}
        icon={<UserIcon />}
        actions={<AddButton onClick={handleCreateUser}>{addButtonText}</AddButton>}
      />

      <TabbedPageCard
        idPrefix="users"
        ariaLabel={`${pageTitle.toLowerCase()} tabs`}
        fullHeight
        panelSx={{ p: 2, overflow: "hidden" }}
        selectedTab={activeTab}
        onTabChange={setActiveTab}
        tabs={tabs}
      />

      <DeleteUserDialog
        open={isDeleteModalOpen}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
        isLoading={isDeleting}
        user={selectedUser}
      />

      <ArchiveUserDialog
        open={isArchiveModalOpen}
        onClose={handleArchiveCancel}
        onConfirm={handleArchiveConfirm}
        isLoading={isArchiving || isRestoring}
        user={selectedUser}
        mode={archiveMode}
      />

      {historyDriver && (
        <AuditHistoryDialog
          open={Boolean(historyDriver)}
          entityType="EmployeeDriverInfo"
          entityId={historyDriver.employeeDriverInfoId}
          isResolvingEntity={historyDriver.isLoading}
          title={historyDriver.title}
          unavailableMessage={historyDriver.unavailableMessage}
          onClose={() => setHistoryDriver(null)}
        />
      )}
    </PageLayout>
  );
};

export default UsersPage;
